import { Box, Grid, Link, Paper, TextField, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useState } from 'react';
import FlightItineraryPassengerDetailsStep from './FlightItineraryPassengerDetailsStep';
import { PassengerInfo } from '~/types';
import { FlightSearchParams } from '~/types';
import FlightItinerarySummaryCard from './FlightItinerarySummaryCard';
import { PassengerValidationMessages } from '~/utils/flight-itinerary-validation';
import { trackButtonClick } from '~/utils/analytics';

type Props = {
  searchParams: FlightSearchParams;
  customerEmail: string;
  passengers: PassengerInfo[];
  customerEmailValidationMessages?: string[];
  passengerValidationMessages?: PassengerValidationMessages;
  onCustomerEmailChange: (value: string) => void;
  onPassengerChange: (passengerId: string, field: keyof PassengerInfo, value: string | Date | null) => void;
  onEditTrip: () => void;
};

const getHelperText = (messages?: string[]) => messages?.join(' ') || undefined;

function FlightItineraryReviewStep({ searchParams, customerEmail, passengers, customerEmailValidationMessages = [], passengerValidationMessages = {}, onCustomerEmailChange, onPassengerChange, onEditTrip }: Props) {
  const [editingPassengerId, setEditingPassengerId] = useState<string | null>(null);
  const [editingCustomerEmail, setEditingCustomerEmail] = useState(false);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Review</Typography>
      <FlightItinerarySummaryCard searchParams={searchParams} onEdit={onEditTrip} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Customer email</Typography>
              <Link
                component="button"
                underline="hover"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                onClick={() => {
                  trackButtonClick('FlightItineraryReviewStep', editingCustomerEmail ? 'Done editing customer email' : 'Edit customer email');
                  setEditingCustomerEmail(!editingCustomerEmail);
                }}
              >
                <EditOutlinedIcon fontSize="small" />
                {editingCustomerEmail ? 'Done' : 'Edit'}
              </Link>
            </Box>
            {editingCustomerEmail ? (
              <TextField
                fullWidth
                required
                type="email"
                label="Customer email"
                value={customerEmail}
                error={customerEmailValidationMessages.length > 0}
                helperText={getHelperText(customerEmailValidationMessages) || 'We will send your itinerary to this email.'}
                sx={{ mt: 2 }}
                onChange={(event) => onCustomerEmailChange(event.target.value)}
              />
            ) : (
              <Typography variant="body2" sx={{ mt: 1 }}><strong>Email:</strong> {customerEmail || '-'}</Typography>
            )}
          </Paper>
        </Grid>
        {passengers.map((p) => (
          <Grid item xs={12} key={p.id}>
            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Passenger {p.id} ({p.passengerType})</Typography>
                <Link
                  component="button"
                  underline="hover"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  onClick={() => {
                    trackButtonClick('FlightItineraryReviewStep', editingPassengerId === p.id ? `Done editing passenger ${p.id}` : `Edit passenger ${p.id}`);
                    setEditingPassengerId(editingPassengerId === p.id ? null : p.id);
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                  {editingPassengerId === p.id ? 'Done' : 'Edit'}
                </Link>
              </Box>
              {editingPassengerId === p.id ? (
                <FlightItineraryPassengerDetailsStep
                  searchParams={searchParams}
                  customerEmail={customerEmail}
                  passengers={[p]}
                  customerEmailValidationMessages={customerEmailValidationMessages}
                  passengerValidationMessages={passengerValidationMessages}
                  onCustomerEmailChange={onCustomerEmailChange}
                  onPassengerChange={onPassengerChange}
                  showContinue={false}
                  onEditTrip={onEditTrip}
                />
              ) : (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2"><strong>Name:</strong> {p.firstName} {p.lastName}</Typography>
                  <Typography variant="body2"><strong>Gender:</strong> {p.gender || '-'}</Typography>
                  <Typography variant="body2"><strong>Date of birth:</strong> {p.dateOfBirth ? p.dateOfBirth.toLocaleDateString() : '-'}</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default FlightItineraryReviewStep;
